import * as turf from "@turf/turf";
import { GameMap } from "../map";
import { GameClient } from "../net";
import { geocodeArea } from "../api";
import { mountAssist } from "../assist";

// Shared Measure tab for the Seeker & Hider shells: a general measure tool
// (two-point line with Start/End locations, or a radius ring with a custom
// drag-to-size handle) plus the "How close am I?" matching-assist panel.
// Expects the markup ids #sk-mmodes, #sk-measure-basic, #sk-assist,
// #sk-measure-note to exist in the panel.

type LL = [number, number];
// Radius presets — ½ mi is the default; "Custom" replaces the (never-used) 10 mi.
const RING_PRESETS: { l: string; m: number }[] = [{ l: "¼", m: 0.25 }, { l: "½", m: 0.5 }, { l: "1", m: 1 }, { l: "3", m: 3 }, { l: "5", m: 5 }];
const fmtMi = (mi: number) => (mi < 0.19 ? `${Math.round(mi * 5280)} ft` : `${mi.toFixed(2)} mi`);
function offsetEast(c: LL, miles: number): LL { return [c[0] + miles / (69 * Math.cos((c[1] * Math.PI) / 180)), c[1]]; }

// Smart NYC geocoding: expand a bare cross-street ("53rd and 3rd") or a partial
// address ("157 E 57th") to something Nominatim resolves, then geocode (the
// worker appends ", New York").
async function smartGeocode(raw: string): Promise<LL> {
  let q = raw.trim();
  const cross = q.match(/^(.+?)\s*(?:and|&|\/)\s*(.+?)$/i);
  if (cross) {
    const isNum = (s: string) => /^\d+\s*(st|nd|rd|th)?$/i.test(s.trim());
    const a = cross[1].trim(), b = cross[2].trim();
    q = `${isNum(a) ? a + " Street" : a} & ${isNum(b) ? b + " Avenue" : b}`;
  }
  if (!/manhattan|new york|\bny\b/i.test(q)) q += ", Manhattan";
  const { geometry } = await geocodeArea(q);
  return turf.centroid(geometry as turf.AllGeoJSON).geometry.coordinates as LL;
}

export interface MeasureTab {
  /** Activate the measure UI in a specific mode (defaults to the last one). */
  enter(mode?: "measure" | "close"): void;
  /** Clears all tool/assist overlays (called when the tab/sub-mode loses focus). */
  leave(): void;
}

export function mountMeasureTab(root: HTMLElement, client: GameClient, gameMap: GameMap, lineHint: string, setFullscreen?: (on: boolean) => void): MeasureTab {
  mountAssist(root.querySelector<HTMLElement>("#sk-assist")!, client, gameMap);

  let mMode: "measure" | "close" = "measure";
  let mSub: "line" | "ring" = "line";
  // Two-point state.
  let startLL: LL | null = null, endLL: LL | null = null;
  // Radius state. Default to a Custom (drag-to-size) ring — the most flexible.
  let centerLL: LL | null = null, radiusMi = 0.5, customRing = true;
  let edgeMarker: ReturnType<GameMap["addToolMarker"]> | null = null;
  // Per-field source label, for the card readout.
  const srcLabel: Record<string, string> = {};
  const basicEl = () => root.querySelector<HTMLElement>("#sk-measure-basic")!;

  function myLocation(): Promise<LL> {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) return reject(new Error("no geolocation"));
      navigator.geolocation.getCurrentPosition((p) => resolve([p.coords.longitude, p.coords.latitude]), () => reject(new Error("denied")), { enableHighAccuracy: true, timeout: 12000, maximumAge: 8000 });
    });
  }

  // ---- two-point line ----------------------------------------------------
  function updateLine() {
    if (startLL && endLL) {
      gameMap.setPreviewGeometry([turf.lineString([startLL, endLL])]);
      // Distance label sits next to the line's origin (the Start point).
      gameMap.setInfoLabel(startLL, fmtMi(turf.distance(startLL, endLL, { units: "miles" })));
    } else { gameMap.clearPreviewGeometry(); gameMap.clearInfoLabel(); }
    renderMeasure();
  }
  function rebuildLine() {
    gameMap.clearToolMarkers();
    if (startLL) gameMap.addToolMarker(startLL, "#0039A6", true, (ll) => { startLL = ll; srcLabel.start = "moved"; updateLine(); }, true, "A");
    if (endLL) gameMap.addToolMarker(endLL, "#EE352E", true, (ll) => { endLL = ll; srcLabel.end = "moved"; updateLine(); }, true, "B");
    updateLine();
  }
  async function setLinePoint(which: "start" | "end", src: "gps" | "tap" | "addr", addr?: string) {
    try {
      let ll: LL;
      if (src === "gps") { ll = await myLocation(); srcLabel[which] = "📍 my location"; }
      else if (src === "addr") { ll = await smartGeocode(addr!); srcLabel[which] = `🔤 ${addr}`; }
      else { // tap — collapse the sheet for a full map, then take the next tap
        setFullscreen?.(true);
        ll = await nextTap();
        setFullscreen?.(false);
        srcLabel[which] = "🗺 tapped";
      }
      if (which === "start") startLL = ll; else endLL = ll;
      rebuildLine();
      gameMap.flyAboveSheet(ll, 14);
    } catch (e) { if ((e as Error).message !== "cancel") alert("Couldn't set that location."); }
  }

  // ---- radius ring -------------------------------------------------------
  function paintRing() {
    if (!centerLL) { gameMap.clearPreviewGeometry(); gameMap.clearInfoLabel(); renderMeasure(); return; }
    gameMap.setPreviewGeometry([turf.circle(centerLL, radiusMi, { units: "miles", steps: 64 })]);
    gameMap.setInfoLabel(centerLL, `${fmtMi(radiusMi)} radius`); // size near the centre
    renderMeasure();
  }
  function rebuildRing() {
    gameMap.clearToolMarkers();
    edgeMarker = null;
    if (!centerLL) { paintRing(); return; }
    // The blue centre is always draggable (moves the whole ring); in custom mode a
    // red edge handle also resizes it. Dragging the centre carries the edge along.
    gameMap.addToolMarker(centerLL, "#0039A6", true, (ll) => {
      centerLL = ll;
      edgeMarker?.setLngLat(offsetEast(centerLL, radiusMi));
      paintRing();
    }, true);
    if (customRing) {
      edgeMarker = gameMap.addToolMarker(offsetEast(centerLL, radiusMi), "#EE352E", true, (ll) => { radiusMi = Math.max(0.05, turf.distance(centerLL!, ll, { units: "miles" })); paintRing(); }, true);
    }
    paintRing();
  }
  async function setRingSource(src: "gps" | "tap") {
    try {
      let ll: LL;
      if (src === "gps") ll = await myLocation();
      else { setFullscreen?.(true); ll = await nextTap(); setFullscreen?.(false); }
      centerLL = ll;
      rebuildRing();
      gameMap.flyAboveSheet(ll, customRing ? 14 : 14);
    } catch (e) { if ((e as Error).message !== "cancel") alert("Couldn't set that location."); }
  }

  // Resolve with the next map tap (used by tap-to-place). Cancels cleanly.
  let cancelTap: (() => void) | null = null;
  function nextTap(): Promise<LL> {
    return new Promise((resolve, reject) => {
      gameMap.captureClicks = true;
      gameMap.onMapClick = (ll) => { cleanup(); resolve([ll.lng, ll.lat]); };
      cancelTap = () => { cleanup(); reject(new Error("cancel")); };
      function cleanup() { gameMap.captureClicks = false; gameMap.onMapClick = null; cancelTap = null; }
    });
  }

  // ---- render ------------------------------------------------------------
  function field(which: "start" | "end", color: string, label: string): string {
    const set = which === "start" ? startLL : endLL;
    return `<div class="meas-field">
      <div class="meas-fhead"><span class="meas-dot" style="background:${color}"></span><b>${label}</b><span class="meas-val">${set ? (srcLabel[which] || "set") : "not set"}</span></div>
      <div class="sk-mode-seg sub meas-srcs">
        <button data-pt="${which}:gps">📍 My location</button>
        <button data-pt="${which}:tap">🗺 Tap map</button>
      </div>
      <div class="meas-addrrow"><input class="sk-search meas-addr" data-addr="${which}" placeholder="or type an address / corner…"/><button class="meas-find" data-find="${which}">Find</button></div>
    </div>`;
  }
  function lineUI(): string {
    const dist = startLL && endLL ? fmtMi(turf.distance(startLL, endLL, { units: "miles" })) : "—";
    return `${field("start", "#0039A6", "Start (A)")}${field("end", "#EE352E", "End (B)")}
      <div class="sk-distpill"><span class="n">${dist}</span><span class="sub">straight-line</span></div>
      <p class="sk-hint2">${lineHint}</p>`;
  }
  function ringUI(): string {
    return `<div class="sk-mode-seg sub">
        <button data-ringsrc="tap">🗺 Tap the map</button>
        <button data-ringsrc="gps">📍 From my location</button>
      </div>
      <div class="sk-seg6">
        ${RING_PRESETS.map((d) => `<button data-ring="${d.m}" class="${!customRing && d.m === radiusMi ? "on" : ""}">${d.l}</button>`).join("")}
        <button data-ring="custom" class="${customRing ? "on" : ""}">Custom</button>
      </div>
      <div class="sk-distpill"><span class="n">${fmtMi(radiusMi)}</span><span class="sub">radius${customRing ? " · drag the red handle" : ""}</span></div>
      <p class="sk-hint2">${centerLL ? "Drag the centre to move the ring." : "Pick a centre to draw the ring."}</p>`;
  }
  function renderMeasure() {
    if (mMode !== "measure") return;
    basicEl().innerHTML = `
      <div class="sk-mode-seg sub"><button data-msub="line" class="${mSub === "line" ? "on" : ""}">Two-point</button><button data-msub="ring" class="${mSub === "ring" ? "on" : ""}">Radius ring</button></div>
      ${mSub === "line" ? lineUI() : ringUI()}`;
    const el = basicEl();
    el.querySelectorAll<HTMLButtonElement>("[data-msub]").forEach((b) => b.addEventListener("click", () => { mSub = b.dataset.msub as "line" | "ring"; startMeasure(); }));
    // line wiring
    el.querySelectorAll<HTMLButtonElement>("[data-pt]").forEach((b) => b.addEventListener("click", () => { const [w, s] = b.dataset.pt!.split(":"); setLinePoint(w as "start" | "end", s as "gps" | "tap"); }));
    el.querySelectorAll<HTMLButtonElement>("[data-find]").forEach((b) => b.addEventListener("click", () => { const w = b.dataset.find as "start" | "end"; const v = el.querySelector<HTMLInputElement>(`[data-addr="${w}"]`)?.value.trim(); if (v) setLinePoint(w, "addr", v); }));
    el.querySelectorAll<HTMLInputElement>("[data-addr]").forEach((inp) => inp.addEventListener("keydown", (e) => { if (e.key === "Enter") { e.preventDefault(); const v = inp.value.trim(); if (v) setLinePoint(inp.dataset.addr as "start" | "end", "addr", v); } }));
    // ring wiring
    el.querySelectorAll<HTMLButtonElement>("[data-ringsrc]").forEach((b) => b.addEventListener("click", () => setRingSource(b.dataset.ringsrc as "gps" | "tap")));
    el.querySelectorAll<HTMLButtonElement>("[data-ring]").forEach((b) => b.addEventListener("click", () => {
      if (b.dataset.ring === "custom") { customRing = true; }
      else { customRing = false; radiusMi = +b.dataset.ring!; }
      rebuildRing();
    }));
  }

  function startMeasure() {
    cancelTap?.();
    gameMap.clearToolMarkers(); gameMap.clearPreviewGeometry(); gameMap.clearInfoLabel();
    gameMap.onToolExit = () => { startLL = null; endLL = null; centerLL = null; measureLeave(); renderMeasure(); };
    if (mSub === "line") {
      // Default Start to "my location" (clearable), End empty until placed.
      const c = gameMap.map.getCenter();
      if (!startLL) { startLL = [c.lng, c.lat]; srcLabel.start = "map centre"; }
      if (!endLL) { endLL = offsetEast(startLL, 0.6); srcLabel.end = "drag me"; }
      rebuildLine();
      myLocation().then((ll) => { startLL = ll; srcLabel.start = "📍 my location"; rebuildLine(); gameMap.flyAboveSheet(ll, 14); }).catch(() => {});
    } else {
      if (!centerLL) { const c = gameMap.map.getCenter(); centerLL = [c.lng, c.lat]; }
      rebuildRing();
    }
    renderMeasure();
  }

  function refreshNote() {
    const note = root.querySelector<HTMLElement>("#sk-measure-note")!;
    const locked = mMode === "close" && !client.config.matchingAssist;
    note.hidden = !locked;
    note.classList.toggle("sk-locked", locked);
    if (locked) note.innerHTML = `🔒 <b>“How close am I?”</b> is turned off for this game.<br>Ask your gamemaster to switch on <b>Matching assist mode</b> to compare your distance to POIs, borders &amp; the coastline.`;
  }
  function setMMode(m: "measure" | "close") {
    mMode = m;
    root.querySelectorAll<HTMLButtonElement>("#sk-mmodes [data-mmode]").forEach((b) => b.classList.toggle("on", b.dataset.mmode === m));
    const off = m === "close" && !client.config.matchingAssist;
    root.querySelector<HTMLElement>("#sk-measure-basic")!.hidden = m !== "measure";
    root.querySelector<HTMLElement>("#sk-assist")!.hidden = m !== "close" || off;
    refreshNote();
    if (m === "measure") { gameMap.clearAssistOverlay(); gameMap.clearReferenceMarker(); startMeasure(); }
    else measureLeave();
  }
  function measureLeave() { cancelTap?.(); gameMap.onToolExit = null; gameMap.clearToolMarkers(); gameMap.clearPreviewGeometry(); gameMap.clearInfoLabel(); gameMap.clearAssistOverlay(); gameMap.clearReferenceMarker(); }
  root.querySelectorAll<HTMLButtonElement>("#sk-mmodes [data-mmode]").forEach((b) => b.addEventListener("click", () => setMMode(b.dataset.mmode as "measure" | "close")));

  return {
    enter(mode?: "measure" | "close") { setMMode(mode ?? mMode); },
    leave: measureLeave,
  };
}
